import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import userService, { UserWithRole, UserIpHistory } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';
import {
  BasicInfoSection,
  AccountInfoSection,
  PremiumInfoSection,
  GameInfoSection,
  SecurityInfoSection,
  PreferencesSection,
} from './UserInfoSections';

interface UserDetailsModalProps {
  user: UserWithRole;
  onClose: () => void;
  onResetPassword?: (userId: number) => Promise<void>;
  resettingPassword?: boolean;
  onSanction?: (userId: number) => void;
  onForceLogout?: (userId: number) => Promise<void>;
  currentUserRoleId?: number;
  currentUserId?: number;
  onlineUserIds?: Set<number>;
  context?: 'roles' | 'users';
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  onClose,
  onResetPassword,
  resettingPassword = false,
  onSanction,
  onForceLogout,
  currentUserRoleId,
  currentUserId,
  onlineUserIds,
  context = 'users'
}) => {
  const { t } = usePageTranslation('panel', 'UsersManagement');
  const [ipHistory, setIpHistory] = useState<UserIpHistory[]>([]);
  const [loadingIpHistory, setLoadingIpHistory] = useState(true);
  const [clearAuthLoading, setClearAuthLoading] = useState(false);
  const [clearAuthMessage, setClearAuthMessage] = useState<string | null>(null);
  const [clearAuthIsError, setClearAuthIsError] = useState(false);

  const canSanction = currentUserRoleId && (
    context === 'roles'
      ? currentUserRoleId === 1
      : currentUserRoleId <= 3
  );

  useEffect(() => {
    const loadIpHistory = async () => {
      try {
        setLoadingIpHistory(true);
        const response = await userService.getUserIpHistory(user.id);
        if (response.success) setIpHistory(response.data || []);
      } catch {
        setIpHistory([]);
      } finally {
        setLoadingIpHistory(false);
      }
    };
    loadIpHistory();
  }, [user.id]);

  const handleResetPassword = async () => {
    if (!onResetPassword) return;
    if (window.confirm(`${t('resetPassword.confirm')} ${user.username}?`)) {
      await onResetPassword(user.id);
    }
  };

  const handleForceLogout = async () => {
    if (!onForceLogout) return;
    if (window.confirm(`Êtes-vous sûr de vouloir déconnecter de force ${user.username} ?`)) {
      await onForceLogout(user.id);
    }
  };

  const canClearAuthRateLimit = Boolean(currentUserRoleId && currentUserRoleId <= 2);

  const handleClearAuthRateLimit = async () => {
    if (!canClearAuthRateLimit) return;
    if (!window.confirm(`Réinitialiser le blocage connexion/inscription pour ${user.username} ?`)) return;
    setClearAuthLoading(true);
    setClearAuthMessage(null);
    setClearAuthIsError(false);
    try {
      const res = await userService.clearAuthRateLimit(user.id);
      if (res.success) {
        setClearAuthIsError(false);
        setClearAuthMessage(res.message || 'Blocage réinitialisé.');
      } else {
        setClearAuthIsError(true);
        setClearAuthMessage(res.error || 'Erreur.');
      }
    } catch (e: any) {
      setClearAuthIsError(true);
      setClearAuthMessage(e?.message || 'Erreur.');
    } finally {
      setClearAuthLoading(false);
    }
  };

  const isOnline = onlineUserIds && onlineUserIds.has(user.id);
  const canForceLogout = onForceLogout && isOnline && user.id !== currentUserId;

  return createPortal(
    <div className="fixed inset-0 bg-cockpit-bg-2/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 pt-24">
      <div className="rounded-xl border border-[#223046]/90 bg-[#060a11] p-6 shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-cockpit-text">
            {t('details.title')} — {user.username}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-cockpit-text-2 hover:text-cockpit-text hover:bg-[#1a2537] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5">
          <BasicInfoSection user={user} t={t} />
          <AccountInfoSection user={user} t={t} />
          <PremiumInfoSection user={user} t={t} />
          <GameInfoSection user={user} t={t} />
          <SecurityInfoSection user={user} ipHistory={ipHistory} loadingIpHistory={loadingIpHistory} t={t} />
          <PreferencesSection user={user} t={t} />

          <div className="rounded-lg border border-[#223046] bg-[#0b1220]/80 p-4">
            <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.actions')}</h4>
            <div className="flex flex-wrap gap-3">
              {onResetPassword && (
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 disabled:opacity-40 transition-colors"
                >
                  {resettingPassword ? t('resetPassword.sending') : t('resetPassword.send')}
                </button>
              )}
              {canClearAuthRateLimit && (
                <button
                  type="button"
                  onClick={handleClearAuthRateLimit}
                  disabled={clearAuthLoading}
                  className="rounded-lg border border-amber-500/40 bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40 transition-colors"
                >
                  {clearAuthLoading ? 'Réinitialisation…' : 'Débloquer tentatives connexion'}
                </button>
              )}
              {canForceLogout && (
                <button
                  onClick={handleForceLogout}
                  className="rounded-lg border border-orange-500/40 bg-orange-500/15 px-4 py-2 text-sm font-semibold text-orange-200 hover:bg-orange-500/25 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('actions.forceLogout') || 'Déconnecter de force'}
                </button>
              )}
              {onSanction && canSanction && (
                <button
                  onClick={() => onSanction(user.id)}
                  className="rounded-lg border border-red-500/35 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-200 hover:bg-red-500/20 transition-colors"
                >
                  {t('details.sanction')}
                </button>
              )}
            </div>
            {onResetPassword && (
              <p className="text-cockpit-text-2 text-xs mt-2">{t('resetPassword.description')}</p>
            )}
            {clearAuthMessage && (
              <p className={`text-xs mt-3 ${clearAuthIsError ? 'text-red-300' : 'text-emerald-300'}`}>{clearAuthMessage}</p>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-5 w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 py-2.5 text-sm font-semibold text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
        >
          {t('details.close')}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default UserDetailsModal;
